import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, IconButton } from '@mui/material';
import { LocalizationProvider, DateCalendar, PickersDay } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import chevronDownIcon from '../../assets/icons/chevron-down.svg';
import chevronUpIcon from '../../assets/icons/chevron-up.svg';
import chevronLeftIcon from '../../assets/icons/chevron-left.svg';
import chevronRightIcon from '../../assets/icons/chevron-right.svg';
import checkIcon from '../../assets/icons/check.svg';

const Rows = ({ 
  columns = [], 
  data = [], 
  onRowClick, 
  expandable = false, 
  onDelete, 
  wrapLayout = false,
  showDatePicker = false,
  onDateRangeChange,
  dateRange = null,
  maxHeight = '400px',
  title = '',
  pagination = null,
  onPageChange = null,
  onRowsPerPageChange = null,
  selectable = false,
  selectedRows = [],
  onSelectionChange = null,
  canSelectRow = null, // Function to determine if a row can be selected
  emptyMessage = null, // Custom empty message
  hideHeader = false, // Hide the header row
  // Sorting props
  sortBy = null,
  sortDirection = 'ASC',
  onSort = null,
  containerPadding = 16,
  isMobile = false, // Mobile/tablet: card layout and compact pagination
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [isRowsDropdownOpen, setIsRowsDropdownOpen] = useState(false);
  const datePickerRef = useRef(null);
  const touchStartPosRef = useRef({ x: 0, y: 0 });

  // Selection logic
  const selectableRows = selectable && canSelectRow ? data.filter(row => canSelectRow(row)) : data;
  const isAllSelected = selectable && selectableRows.length > 0 && selectedRows.length === selectableRows.length;
  const isIndeterminate = selectable && selectedRows.length > 0 && selectedRows.length < selectableRows.length;

  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    
    if (isAllSelected) {
      // Deselect all
      onSelectionChange([]);
    } else {
      // Select all selectable rows
      onSelectionChange(selectableRows.map(row => row.id));
    }
  };

  const handleRowSelect = (rowId, isSelected) => {
    if (!onSelectionChange) return;
    
    // Check if this row can be selected
    const row = data.find(r => r.id === rowId);
    if (canSelectRow && !canSelectRow(row)) {
      return; // Don't allow selection of non-selectable rows
    }
    
    if (isSelected) {
      onSelectionChange([...selectedRows, rowId]);
    } else {
      onSelectionChange(selectedRows.filter(id => id !== rowId));
    }
  };

  // Sorting handler
  const handleSort = (columnKey) => {
    if (!onSort) return;
    
    let newDirection = 'ASC';
    if (sortBy === columnKey && sortDirection === 'ASC') {
      newDirection = 'DESC';
    }
    
    onSort(columnKey, newDirection);
  };

  const toggleRow = (rowIndex) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex],
    }));
  };

  // Date picker helper functions
  const isDateInRange = (date, start, end) => {
    if (!start || !end) return false;
    const dateTime = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const startTime = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    const endTime = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
    
    return dateTime > startTime && dateTime < endTime;
  };

  const isDateStartOrEnd = (date, start, end) => {
    if (!start) return false;
    const dateTime = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    const startTime = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
    
    if (!end) {
      return dateTime === startTime;
    }
    
    const endTime = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
    return dateTime === startTime || dateTime === endTime;
  };

  // Custom day component for range highlighting - touch-friendly
  const CustomPickersDay = (props) => {
    const { day, ...other } = props;
    const isInRange = endDate ? isDateInRange(day, startDate, endDate) : false;
    const isStartOrEnd = isDateStartOrEnd(day, startDate, endDate);
    
    return (
      <PickersDay
        {...other}
        day={day}
        onTouchEnd={(e) => {
          e.preventDefault();
          const btn = e.currentTarget;
          if (btn && typeof btn.click === 'function') btn.click();
        }}
        sx={{
          fontFamily: 'Noto Sans',
          fontWeight: 700,
          color: '#000000',
          borderRadius: '12px',
          touchAction: 'manipulation',
          '@media (max-width: 768px)': { minWidth: 44, minHeight: 44 },
          ...(isInRange && {
            backgroundColor: '#E5E5E5',
            color: '#666666',
            '&:hover': {
              backgroundColor: '#D5D5D5',
            },
          }),
          ...(isStartOrEnd && {
            backgroundColor: '#027BC6',
            color: 'white',
            boxShadow: '0px 4px 10px #6A78F819',
            '&:hover': {
              backgroundColor: '#027BC6',
            },
          }),
        }}
      />
    );
  };

  const handleDateSelection = (selectedDate) => {
    if (!startDate) {
      setStartDate(selectedDate);
      setEndDate(null);
    } else if (!endDate) {
      if (selectedDate >= startDate) {
        setEndDate(selectedDate);
      } else {
        setStartDate(selectedDate);
        setEndDate(null);
      }
    } else {
      setStartDate(selectedDate);
      setEndDate(null);
    }
  };

  const handleDateRangeApply = () => {
    if (startDate && endDate && onDateRangeChange) {
      onDateRangeChange({
        start: startDate,
        end: endDate
      });
    } else if (startDate && onDateRangeChange) {
      // If only start date is selected, use it as both start and end
      onDateRangeChange({
        start: startDate,
        end: startDate
      });
    }
    setIsDatePickerOpen(false);
  };

  const handleDateRangeCancel = () => {
    setIsDatePickerOpen(false);
    // Reset to original values if they exist
    if (dateRange && dateRange.start && dateRange.end) {
      setStartDate(new Date(dateRange.start));
      setEndDate(new Date(dateRange.end));
    }
  };

  const handleDateRangeReset = () => {
    setStartDate(null);
    setEndDate(null);
    setIsDatePickerOpen(false);
    if (onDateRangeChange) {
      onDateRangeChange(null);
    }
  };

  const getDateRangeDisplay = () => {
    if (startDate && endDate) {
      // Check if both dates are today
      const today = new Date();
      const isStartToday = startDate.toDateString() === today.toDateString();
      const isEndToday = endDate.toDateString() === today.toDateString();
      
      if (isStartToday && isEndToday) {
        return 'Aujourd\'hui';
      }
      
      const formatDateForDisplay = (date) => {
        if (!date) return '';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };
      
      return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`;
    }
    return 'Aujourd\'hui';
  };

  // Initialize dates from props
  useEffect(() => {
    if (dateRange && dateRange.start && dateRange.end) {
      setStartDate(new Date(dateRange.start));
      setEndDate(new Date(dateRange.end));
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  }, [dateRange]);

  // Close date picker when clicking outside (mouse only - no touch listener so tap-to-open isn't treated as tap-outside)
  useEffect(() => {
    if (!isDatePickerOpen) return;

    const handleClickOutside = (e) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setIsDatePickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDatePickerOpen]);

  // Close rows dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isRowsDropdownOpen) {
        // Check if click is outside the dropdown
        const dropdownElement = event.target.closest('[data-dropdown]');
        if (!dropdownElement) {
          setIsRowsDropdownOpen(false);
        }
      }
    };

    if (isRowsDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isRowsDropdownOpen]);

  return (
    <div
      className={`w-full ${wrapLayout ? "overflow-y-auto overflow-x-hidden" : "overflow-x-auto"}`}
      style={{ paddingLeft: `${containerPadding}px`, paddingRight: `${containerPadding}px`, boxSizing: 'border-box' }}
    >
      {/* Title and Date Picker Header */}
      {title && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}
        >
          {/* Title */}
          <Typography
            style={{
              fontFamily: 'Montserrat',
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '32px',
              letterSpacing: '0px',
              color: '#38485C',
              textTransform: 'none',
              opacity: 0.88,
              margin: 0
            }}
          >
            {title}
          </Typography>

          {/* Date Picker Trigger */}
          {showDatePicker && (
            <Box
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: '#027BC6',
                textDecoration: 'underline',
                touchAction: 'manipulation',
              }}
              onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
            >
              <Typography
                style={{
                  fontFamily: 'Montserrat',
                  fontWeight: 600,
                  fontSize: '14px',
                  color: '#027BC6',
                  margin: 0,
                  marginRight: '8px'
                }}
              >
                Date: {getDateRangeDisplay()}
              </Typography>
              <img
                src={chevronDownIcon}
                alt="chevron"
                style={{
                  width: '12px',
                  height: '12px',
                  transform: isDatePickerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease',
                  filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(188deg) brightness(89%) contrast(101%)'
                }}
              />
            </Box>
          )}
        </div>
      )}

      {/* Header */}
      {!hideHeader && (
      <div
        className={`w-full flex items-center pt-4 pb-2 px-[16px] text-[#919191] text-[12px] font-semibold 
    ${wrapLayout ? "flex-wrap" : ""}`}
        style={{}}
      >
        {selectable && (
          <div className="flex-none w-[40px] flex items-center justify-center" style={{ position: 'relative' }}>
            <input
              type="checkbox"
              checked={isAllSelected}
              ref={(input) => {
                if (input) input.indeterminate = isIndeterminate;
              }}
              onChange={handleSelectAll}
              style={{
                width: '16px',
                height: '16px',
                border: '1px solid #C5C5C5',
                borderRadius: '4px',
                opacity: 1,
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                backgroundColor: isAllSelected || isIndeterminate ? '#007BC6' : 'white',
                cursor: 'pointer',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                if (!isAllSelected && !isIndeterminate) {
                  e.target.style.borderColor = '#007BC6';
                  e.target.style.backgroundColor = '#F0F8FF';
                }
              }}
              onMouseLeave={(e) => {
                if (!isAllSelected && !isIndeterminate) {
                  e.target.style.borderColor = '#C5C5C5';
                  e.target.style.backgroundColor = 'white';
                }
              }}
            />
            {isAllSelected && (
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '14px',
                  height: '14px',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <img
                  src={checkIcon}
                  alt="check"
                  style={{
                    width: '10px',
                    height: '10px',
                    filter: 'brightness(0) invert(1) drop-shadow(0 0 0.5px white)'
                  }}
                />
              </div>
            )}
            {isIndeterminate && (
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '8px',
                  height: '2px',
                  backgroundColor: 'white',
                  borderRadius: '1px',
                  pointerEvents: 'none'
                }}
              />
            )}
          </div>
        )}
        
        {expandable && data.some(row => row.productQuantities?.length > 0 || row.products?.length > 0) && (
          <div className="flex-none w-[40px]"></div>
        )}

        {columns.map((column, index) => {
          const isActionsColumn = column.key === 'actions';
          const isSortable = column.sortable && onSort;
          const isCurrentlySorted = sortBy === column.key;
          
          return (
            <div
              key={index}
              className={`px-6 ${isActionsColumn ? '' : 'text-left'} 
          ${isActionsColumn 
            ? 'flex items-center justify-center flex-none' 
            : column.key === 'description' 
              ? `${wrapLayout ? "break-words" : ""}`
              : column.key === 'amount' || column.key === 'montant'
                ? `flex-none w-[200px] ${wrapLayout ? "break-words" : ""}`
                : column.key === 'status' || column.key === 'statut'
                  ? 'flex items-center justify-center flex-none'
                  : column.width 
                    ? `flex-none ${wrapLayout ? "break-words" : ""}`
                    : `flex-1 ${wrapLayout ? "break-words" : "min-w-[150px]"}`}`}
              style={{
                ...(isActionsColumn && column.width ? { width: column.width, flexShrink: 0, flexGrow: 0 } : {}),
                ...(column.width && !isActionsColumn ? { width: column.width } : {}),
                ...(column.key === 'active' ? { minWidth: '80px' } : {})
              }}
            >
              {isSortable ? (
                <div
                  className="flex items-center gap-1 cursor-pointer hover:text-[#027BC6] transition-colors duration-200"
                  onClick={() => handleSort(column.key)}
                  style={{
                    color: isCurrentlySorted ? '#027BC6' : '#919191'
                  }}
                >
                  <span>{column.label}</span>
                  {isCurrentlySorted && (
                    <img
                      src={sortDirection === 'ASC' ? chevronUpIcon : chevronDownIcon}
                      alt={sortDirection === 'ASC' ? 'ascending' : 'descending'}
                      style={{
                        width: '12px',
                        height: '12px',
                        filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(188deg) brightness(89%) contrast(101%)'
                      }}
                    />
                  )}
                </div>
              ) : (
                column.label
              )}
            </div>
          );
        })}
      </div>
      )}

      {/* Data Rows */}
      <div className="pb-4">
        {data.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#919191'
            }}
          >
              <div
                style={{
                  fontFamily: 'Montserrat',
                  fontWeight: 500,
                  fontSize: '14px',
                  color: '#919191',
                  margin: 0
                }}
              >
                {emptyMessage || 'Aucune donnée disponible'}
              </div>
          </div>
        ) : isMobile ? (
          /* Mobile/tablet card layout - same data and actions, no horizontal scroll */
          data.map((row, rowIndex) => (
            <div key={rowIndex} className="mb-3 w-full">
              <div
                className={`w-full rounded-[16px] transition-colors duration-200 ${selectedRows.includes(row.id) ? 'border-[1px] border-[#007BC6] bg-blue-50' : ''} ${onRowClick ? 'cursor-pointer hover:bg-blue-50' : ''}`}
                style={{
                  borderRadius: '16px',
                  backgroundClip: 'padding-box',
                  boxShadow: '0 3px 8px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
                  background: 'white',
                  padding: '16px',
                  touchAction: 'manipulation',
                }}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onTouchStart={onRowClick ? (e) => { touchStartPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; } : undefined}
                onTouchEnd={onRowClick ? (e) => {
                  const t = e.changedTouches[0];
                  const dx = t.clientX - touchStartPosRef.current.x;
                  const dy = t.clientY - touchStartPosRef.current.y;
                  if (dx * dx + dy * dy < 100) { e.preventDefault(); onRowClick(row); }
                } : undefined}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {selectable && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id)}
                        disabled={canSelectRow && !canSelectRow(row)}
                        onChange={(e) => handleRowSelect(row.id, e.target.checked)}
                        style={{
                          width: '20px',
                          height: '20px',
                          border: '1px solid #C5C5C5',
                          borderRadius: '4px',
                          opacity: canSelectRow && !canSelectRow(row) ? 0.3 : 1,
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          backgroundColor: selectedRows.includes(row.id) ? '#007BC6' : 'white',
                          cursor: canSelectRow && !canSelectRow(row) ? 'not-allowed' : 'pointer',
                        }}
                      />
                      <Typography component="span" sx={{ fontSize: '12px', color: '#919191', fontWeight: 600 }}>Sélectionner</Typography>
                    </Box>
                  )}
                  {columns.map((col) => {
                    if (col.key === 'actions') {
                      return (
                        <Box key={col.key} onClick={(e) => e.stopPropagation()} sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 0.5 }}>
                          {col.render ? col.render(row[col.key], row) : null}
                        </Box>
                      );
                    }
                    const value = col.render ? col.render(row[col.key], row) : (row[col.key] ?? 'N/A');
                    return (
                      <Box key={col.key}>
                        <Typography sx={{ fontSize: '12px', color: '#919191', fontWeight: 600, marginBottom: 0.25 }}>{col.label}</Typography>
                        <Box sx={{ fontSize: '16px', fontWeight: 600, color: '#38485C', wordBreak: 'break-word' }}>{value}</Box>
                      </Box>
                    );
                  })}
                </Box>
              </div>
            </div>
          ))
        ) : (
          data.map((row, rowIndex) => (
            <div key={rowIndex} className="mb-2 w-full">
              {/* Main Row */}
              <div
                className={`flex items-center bg-white py-2 px-4 rounded-[16px] min-h-[56px] transition-colors duration-200 w-full
                  ${expandedRows[rowIndex] ? "border-[1px] border-[rgba(107,119,248,0.5)]" : ""} 
                  ${selectedRows.includes(row.id) ? "border-[1px] border-[#007BC6] bg-blue-50" : ""}
                  ${wrapLayout ? "flex-wrap" : ""}
                  ${onRowClick ? "cursor-pointer hover:bg-blue-50" : ""}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                onTouchStart={onRowClick ? (e) => { touchStartPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; } : undefined}
                onTouchEnd={onRowClick ? (e) => {
                  const t = e.changedTouches[0];
                  const dx = t.clientX - touchStartPosRef.current.x;
                  const dy = t.clientY - touchStartPosRef.current.y;
                  if (dx * dx + dy * dy < 100) { e.preventDefault(); onRowClick(row); }
                } : undefined}
                style={{
                  borderRadius: '16px',
                  backgroundClip: 'padding-box',
                  boxShadow: '0 3px 8px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
                  touchAction: 'manipulation',
                  ...(row.hasActiveSession ? { 
                    borderLeft: '4px solid #0AD69F',
                    borderTopLeftRadius: '16px',
                    borderBottomLeftRadius: '16px'
                  } : {})
                }}
              >
                  {selectable && (
                    <div className="flex-none w-[40px] flex items-center justify-center" style={{ position: 'relative' }}>
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id)}
                        disabled={canSelectRow && !canSelectRow(row)}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleRowSelect(row.id, e.target.checked);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        style={{
                          width: '16px',
                          height: '16px',
                          border: '1px solid #C5C5C5',
                          borderRadius: '4px',
                          opacity: canSelectRow && !canSelectRow(row) ? 0.3 : 1,
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          backgroundColor: selectedRows.includes(row.id) ? '#007BC6' : 'white',
                          cursor: canSelectRow && !canSelectRow(row) ? 'not-allowed' : 'pointer',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          if (!selectedRows.includes(row.id) && !(canSelectRow && !canSelectRow(row))) {
                            e.target.style.borderColor = '#007BC6';
                            e.target.style.backgroundColor = '#F0F8FF';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!selectedRows.includes(row.id) && !(canSelectRow && !canSelectRow(row))) {
                            e.target.style.borderColor = '#C5C5C5';
                            e.target.style.backgroundColor = 'white';
                          }
                        }}
                      />
                      {selectedRows.includes(row.id) && (
                        <div
                          style={{
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '14px',
                            height: '14px',
                            pointerEvents: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <img
                            src={checkIcon}
                            alt="check"
                            style={{
                              width: '10px',
                              height: '10px',
                              filter: 'brightness(0) invert(1) drop-shadow(0 0 0.5px white)' // Makes the SVG white and bolder
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  
                  {expandable && (
                    <button
                      className="flex-none w-[40px] flex items-center justify-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleRow(rowIndex);
                      }}
                      aria-label={expandedRows[rowIndex] ? "Réduire" : "Développer"}
                    >
                      <img
                        src={chevronDownIcon}
                        alt=""
                        className={`w-5 h-5 transition-transform duration-200 ${expandedRows[rowIndex] ? 'rotate-180' : ''}`}
                      />
                    </button>
                  )}

                  {columns.map((col, colIndex) => {
                    const isActionsColumn = col.key === 'actions';
                    return (
                      <div
                        key={colIndex}
                        className={`px-6 ${isActionsColumn || col.key === 'status' || col.key === 'statut' ? '' : 'text-left'} text-[#38485C] text-[16px] font-semibold
          ${isActionsColumn 
            ? 'flex items-center justify-center flex-none' 
            : col.key === 'description' 
              ? `${wrapLayout ? "break-words" : ""}`
              : col.key === 'amount' || col.key === 'montant'
                ? `flex-none w-[200px] ${wrapLayout ? "break-words" : ""}`
                : col.key === 'status' || col.key === 'statut'
                  ? 'flex items-center justify-center flex-none'
                  : col.width 
                    ? `flex-none ${wrapLayout ? "break-words" : ""}`
                    : `flex-1 ${wrapLayout ? "break-words" : "min-w-[150px]"}`}`}
                        style={{
                          ...(col.key === 'description' && {
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }),
                          ...(col.key === 'Caisse' && {
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                          }),
                          ...(isActionsColumn && col.width && { width: col.width, flexShrink: 0, flexGrow: 0 }),
                          ...(col.width && !isActionsColumn ? { width: col.width } : {}),
                          ...(col.key === 'active' ? { minWidth: '80px' } : {})
                        }}
                      >
                        {col.render
                          ? col.render(row[col.key], row)
                          : row[col.key] || "N/A"}
                      </div>
                    );
                  })}
                </div>
              

              {expandable &&
                expandedRows[rowIndex] &&
                (row.productQuantities?.length > 0 || row.products?.length > 0) && (
                  <div className="mt-2 pt-[8px] pl-8">
                    <div className="flex flex-col gap-4">
                      {(row.productQuantities || row.products).map((product, productIndex) => {
                        const reference = product.productReference || product.product?.reference || "N/A";
                        const name = product.productName || product.product?.name || "Unknown";
                        const quantity = product.quantity || "N/A";

                        return (
                          <div
                            key={productIndex}
                            className="flex items-center bg-white py-1 px-4 rounded-[16px]"
                            style={{ borderRadius: '16px', backgroundClip: 'padding-box', boxShadow: '0 3px 8px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)' }}
                          >
                            {/* Product Details */}
                            <div className="flex flex-wrap w-full gap-x-4 gap-y-2">
                              <div className="flex-1 max-w-[250px]">
                                <p className="text-[#707070] text-[12px] font-semibold">Référence</p>
                                <p className="text-[#38485C] text-[16px] font-semibold break-words">{reference}</p>
                              </div>

                              <div className="flex-1 max-w-[450px]">
                                <p className="text-[#707070] text-[12px] font-semibold">Nom</p>
                                <p className="text-[#38485C] text-[16px] font-semibold break-words">{name}</p>
                              </div>

                              <div className="flex-1 max-w-[250px]">
                                <p className="text-[#707070] text-[12px] font-semibold">Quantité</p>
                                <p className="text-[#38485C] text-[16px] font-semibold">{quantity}</p>
                              </div>

                              {row.products && row.products.length > 0 && (
                                <div className="flex-1 max-w-[200px]">
                                  <p className="text-[#707070] text-[12px] font-semibold">Prix d'achat</p>
                                  <p className="text-[#38485C] text-[16px] font-semibold">{product.price || "N/A"}</p>
                                </div>
                              )}

                              {row.products?.length > 0 && (
                                <div className="flex justify-end items-start max-w-[50px] mr-10">
                                  <button
                                    className="w-8 h-8 flex items-center justify-center bg-white rounded-[16px] shadow-[0px_4px_16px_rgba(98,107,125,0.15)]"
                                    onClick={() => onDelete && onDelete(row, product)}
                                    aria-label="Supprimer"
                                  >
                                    <span className="text-red-500 text-lg">×</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
            </div>
          ))
        )}
      </div>

      {/* Date Picker Modal */}
      {isDatePickerOpen && (
        <Box
          ref={datePickerRef}
          sx={{
            position: 'absolute',
            top: '60px',
            right: '0',
            zIndex: 9999,
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
            padding: 0,
            minWidth: { xs: '280px', sm: '320px' },
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 'none' },
            width: 'max-content',
            overflow: 'hidden',
            border: '1px solid #E3E3E3',
            pointerEvents: 'auto'
          }}
        >
          <LocalizationProvider 
            dateAdapter={AdapterDateFns} 
            adapterLocale={fr}
          >
            {/* Single Calendar */}
            <DateCalendar
              value={startDate}
              onChange={handleDateSelection}
              slots={{ day: CustomPickersDay }}
              slotProps={{
                calendarHeader: {
                  sx: {
                    '& .MuiPickersCalendarHeader-label': {
                      textTransform: 'none',
                      fontFamily: 'Montserrat',
                      fontWeight: 700,
                      fontSize: '20px',
                    },
                    '& .MuiPickersArrowSwitcher-root': {
                      '& .MuiIconButton-root': {
                        color: '#000000',
                        fontWeight: 'bold',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      },
                    },
                  },
                },
              }}
              sx={{
                '& .MuiDayCalendar-weekDayLabel': {
                  fontFamily: 'Avenir',
                  fontWeight: 900,
                  fontSize: '10px',
                  lineHeight: '16px',
                  letterSpacing: '0.2px',
                  color: '#3E3E3E',
                  opacity: 0.5,
                  width: '40px !important',
                  minWidth: '40px !important',
                  flex: '0 0 40px !important',
                  textAlign: 'center !important',
                  margin: '0 2px !important',
                },
                '& .MuiDayCalendar-weekContainer': {
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  gap: '4px',
                },
                '& .MuiPickersDay-root': {
                  width: '40px !important',
                  height: '40px !important',
                  margin: '0 !important',
                  display: 'flex !important',
                  alignItems: 'center !important',
                  justifyContent: 'center !important',
                  minWidth: '40px !important',
                  flex: '0 0 40px !important',
                  touchAction: 'manipulation',
                  '@media (max-width: 768px)': {
                    width: '44px !important',
                    height: '44px !important',
                    minWidth: '44px !important',
                    flex: '0 0 44px !important',
                  },
                },
              }}
            />

            {/* Action Buttons */}
            <Box sx={{ p: 1 }}>
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 0.5, sm: 1 }, 
                justifyContent: 'flex-end',
                flexWrap: { xs: 'wrap', sm: 'nowrap' }
              }}>
                <button
                  onClick={handleDateRangeReset}
                  onTouchEnd={(e) => { e.preventDefault(); handleDateRangeReset(); }}
                  style={{
                    padding: window.innerWidth < 600 ? '6px 12px' : '8px 16px',
                    backgroundColor: 'transparent',
                    color: '#919191',
                    border: 'none',
                    borderRadius: '12px',
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    fontSize: window.innerWidth < 600 ? '12px' : '14px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textTransform: 'none',
                    height: '34px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    touchAction: 'manipulation',
                  }}
                >
                  Réinitialiser
                </button>
                <button
                  onClick={handleDateRangeCancel}
                  onTouchEnd={(e) => { e.preventDefault(); handleDateRangeCancel(); }}
                  style={{
                    padding: window.innerWidth < 600 ? '6px 12px' : '8px 16px',
                    backgroundColor: 'transparent',
                    color: '#027BC6',
                    border: 'none',
                    borderRadius: '12px',
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    fontSize: window.innerWidth < 600 ? '12px' : '14px',
                    cursor: 'pointer',
                    textTransform: 'none',
                    height: '34px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    touchAction: 'manipulation',
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleDateRangeApply}
                  onTouchEnd={(e) => { e.preventDefault(); handleDateRangeApply(); }}
                  style={{
                    padding: window.innerWidth < 600 ? '6px 12px' : '8px 16px',
                    backgroundColor: '#027BC6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0px 4px 10px #6A78F819',
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    fontSize: window.innerWidth < 600 ? '12px' : '14px',
                    cursor: 'pointer',
                    width: '104px',
                    height: '34px',
                    textTransform: 'none',
                    touchAction: 'manipulation',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#025a9c';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#027BC6';
                  }}
                >
                  Appliquer
                </button>
              </Box>
            </Box>
          </LocalizationProvider>
        </Box>
      )}

      {/* Custom Pagination Controls - compact on mobile */}
      {pagination && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile ? 'stretch' : 'center',
            gap: isMobile ? 2 : 0,
            py: 2,
            px: 2,
          }}
        >
          {/* Left side - Show rows dropdown + count */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
            <Typography variant="body2" sx={{ color: '#666', fontSize: isMobile ? '12px' : '14px' }}>
              Afficher les lignes
            </Typography>
            <Box sx={{ position: 'relative' }} data-dropdown>
              <Box 
                onClick={() => setIsRowsDropdownOpen(!isRowsDropdownOpen)}
                onTouchEnd={(e) => { e.preventDefault(); setIsRowsDropdownOpen(open => !open); }}
                sx={{
                  width: '60px',
                  height: '32px',
                  background: '#FFFFFF',
                  boxShadow: '0px 20px 80px #00000011',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  gap: 0.5,
                  touchAction: 'manipulation',
                }}
              >
                <Typography variant="body2" sx={{ fontSize: '12px', fontWeight: 600 }}>
                  {pagination.size}
                </Typography>
                <img 
                  src={chevronDownIcon} 
                  alt="dropdown" 
                  style={{ width: '8px', height: '8px' }}
                />
              </Box>
              
              {/* Dropdown menu - appears above */}
              {isRowsDropdownOpen && (
                <Box sx={{
                  position: 'absolute',
                  bottom: '100%',
                  left: 0,
                  background: '#FFFFFF',
                  boxShadow: '0px 20px 80px #00000011',
                  borderRadius: '12px',
                  zIndex: 1000,
                  minWidth: '60px',
                  marginBottom: '4px'
                }}>
                  {[10, 20, 50, 100, 200].map((size) => (
                    <Box
                      key={size}
                      onClick={() => {
                        if (onRowsPerPageChange) {
                          onRowsPerPageChange(size);
                        } else if (pagination?.onSizeChange) {
                          pagination.onSizeChange(size);
                        }
                        setIsRowsDropdownOpen(false);
                      }}
                      onTouchEnd={(e) => {
                        e.preventDefault();
                        if (onRowsPerPageChange) onRowsPerPageChange(size);
                        else if (pagination?.onSizeChange) pagination.onSizeChange(size);
                        setIsRowsDropdownOpen(false);
                      }}
                      sx={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: 600,
                        touchAction: 'manipulation',
                        '&:hover': {
                          background: '#f5f5f5'
                        },
                        '&:first-of-type': {
                          borderTopLeftRadius: '12px',
                          borderTopRightRadius: '12px'
                        },
                        '&:last-of-type': {
                          borderBottomLeftRadius: '12px',
                          borderBottomRightRadius: '12px'
                        }
                      }}
                    >
                      {size}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
            
            {/* Row count next to dropdown */}
            <Typography variant="body2" sx={{ color: '#666', fontSize: isMobile ? '12px' : '14px' }}>
              {pagination.page * pagination.size + 1}-{Math.min((pagination.page + 1) * pagination.size, pagination.total)} sur {pagination.total}
            </Typography>
          </Box>

          {/* Page navigation - wrap on mobile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: isMobile ? 'center' : 'flex-start' }}>
            {/* Previous button */}
            <IconButton
              onClick={() => {
                if (onPageChange) {
                  onPageChange(pagination.page - 1);
                } else if (pagination?.onPageChange) {
                  pagination.onPageChange(pagination.page - 1);
                }
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                if (pagination.page > 0) {
                  if (onPageChange) onPageChange(pagination.page - 1);
                  else if (pagination?.onPageChange) pagination.onPageChange(pagination.page - 1);
                }
              }}
              disabled={pagination.page === 0}
              sx={{
                touchAction: 'manipulation',
                width: isMobile ? '44px' : '40px',
                height: isMobile ? '44px' : '40px',
                minWidth: isMobile ? 44 : 40,
                minHeight: isMobile ? 44 : 40,
                background: '#FFFFFF',
                boxShadow: '0px 20px 80px #00000011',
                borderRadius: '12px',
                '&:hover': {
                  background: '#f5f5f5'
                },
                '&:disabled': {
                  opacity: 0.5
                }
              }}
            >
              <img 
                src={chevronLeftIcon} 
                alt="previous" 
                style={{ width: '16px', height: '16px' }}
              />
            </IconButton>

            {/* Page numbers */}
            {(() => {
              const totalPages = Math.ceil(pagination.total / pagination.size);
              const currentPage = pagination.page + 1;
              const pages = [];
              
              // Show first few pages
              for (let i = 1; i <= Math.min(4, totalPages); i++) {
                pages.push(i);
              }
              
              // Add ellipsis if needed
              if (totalPages > 4) {
                pages.push('...');
                pages.push(totalPages);
              }
              
              return pages.map((page, index) => (
                <Box
                  key={index}
                  onClick={() => {
                    if (page !== '...') {
                      if (onPageChange) {
                        onPageChange(page - 1);
                      } else if (pagination?.onPageChange) {
                        pagination.onPageChange(page - 1);
                      }
                    }
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    if (page !== '...') {
                      if (onPageChange) onPageChange(page - 1);
                      else if (pagination?.onPageChange) pagination.onPageChange(page - 1);
                    }
                  }}
                  sx={{
                    width: isMobile ? '44px' : '40px',
                    touchAction: 'manipulation',
                    height: isMobile ? '44px' : '40px',
                    minWidth: isMobile ? 44 : 40,
                    minHeight: isMobile ? 44 : 40,
                    background: page === currentPage ? '#027BC6' : '#FFFFFF',
                    color: page === currentPage ? '#FFFFFF' : '#000000',
                    boxShadow: '0px 20px 80px #00000011',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: page === '...' ? 'default' : 'pointer',
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: 600,
                    '&:hover': page !== '...' ? {
                      background: page === currentPage ? '#025a9c' : '#f5f5f5'
                    } : {}
                  }}
                >
                  {page}
                </Box>
              ));
            })()}

            {/* Next button */}
            <IconButton
              onClick={() => {
                if (onPageChange) {
                  onPageChange(pagination.page + 1);
                } else if (pagination?.onPageChange) {
                  pagination.onPageChange(pagination.page + 1);
                }
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                const totalPages = Math.ceil(pagination.total / pagination.size) - 1;
                if (pagination.page < totalPages) {
                  if (onPageChange) onPageChange(pagination.page + 1);
                  else if (pagination?.onPageChange) pagination.onPageChange(pagination.page + 1);
                }
              }}
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.size) - 1}
              sx={{
                touchAction: 'manipulation',
                width: isMobile ? '44px' : '40px',
                height: isMobile ? '44px' : '40px',
                minWidth: isMobile ? 44 : 40,
                minHeight: isMobile ? 44 : 40,
                background: '#FFFFFF',
                boxShadow: '0px 20px 80px #00000011',
                borderRadius: '12px',
                '&:hover': {
                  background: '#f5f5f5'
                },
                '&:disabled': {
                  opacity: 0.5
                }
              }}
            >
              <img 
                src={chevronRightIcon} 
                alt="next" 
                style={{ width: '16px', height: '16px' }}
              />
            </IconButton>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default Rows;
